import type { FilteredProduct } from '@/api/filteredProducts';
import { isRankDecoration, isStockDecoration } from '@/api/filteredProducts';
import { validateAndUpdateMedicationPadItemDetailUtil } from '@/components/Patient/components/PrescriptionItemEditor/utils/validateAndUpdateMedicationPadItemDetail.utils';
import { useGetProductSurvey } from '@/hooks/rest/useGetProductSurvey';
import type { RadioOption } from '@/shared-ui/FeedbackRadioGroup/FeedbackRadioGroup';
import type { Filters } from '@/shared-ui/ProductFilter/ProductFilter';
import ProductSelection from '@/shared-ui/ProductSelection/ProductSelection';
import { useAppStore, useMedicationPadItemSelector, usePrescriptionPadRulesSelector } from '@/state-management';
import { getFormulationFilter } from '@/utils/dosageLimit/getFormulationFilter';
import { Logger } from '@/utils/logger';
import { RankTier } from '@montugroup/pms-api-contracts';
import { Alert } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MedicationPadItemType } from '../../PrescriptionPad';
import { getAnswerDisplayPositions } from '../../PrescriptionPad';
import {
  PRODUCT_INTERVAL_DEFAULT,
  PRODUCT_QUANTITY_DEFAULT,
  PRODUCT_REPEATS_DEFAULT,
} from '../../ProductDetailsCard/ProductDetailsCard.constants';
import type { ProductSelectionFeedbackProps } from '../../ProductSelectionFeedback/ProductSelectionFeedback';
import ProductSelectionFeedback from '../../ProductSelectionFeedback/ProductSelectionFeedback';
import { usePrescriptionItemFeatureFlags } from '../hooks/usePrescriptionItemFeatureFlags';
import { SHOW_MORE_PRODUCTS_BUTTON_LABEL } from '../MedicationPadItemEditor.constants';
import type { PriceTier } from '../MedicationPadItemEditor.types';
import { mapPricingSchemaToProductCard } from '../MedicationPadItemEditor.utils';
import { getPadItemDetailFromProduct } from '../utils/getPadItemDetailFromProduct';

const logger = new Logger('PrescriptionItemProductSelection');

interface PrescriptionItemProductSelectionProps {
  medicationPadItemId: string;
  doctorId: number;
  showViewMoreProducts: boolean;
  medicationOptions: FilteredProduct[];
  selectedMedication: FilteredProduct | undefined;
  filteredMedicationOptions: FilteredProduct[];
  selectedFilters?: Filters;
  showPricing?: boolean;
  selectedPriceTier?: PriceTier | null;
  setShowViewMoreProducts: (show: boolean) => void;
  handlePreviewIconClick: ((medication: FilteredProduct) => void) | undefined;
}

export const PrescriptionItemProductSelection = ({
  medicationPadItemId,
  doctorId,
  showViewMoreProducts,
  medicationOptions,
  selectedMedication,
  selectedFilters,
  filteredMedicationOptions,
  showPricing,
  selectedPriceTier,
  setShowViewMoreProducts,
  handlePreviewIconClick,
}: PrescriptionItemProductSelectionProps) => {
  const { recommendedMedicationsCount } = usePrescriptionItemFeatureFlags();
  const prescriptionPadRules = usePrescriptionPadRulesSelector();
  const [isMedicationSelectionOpen, setIsMedicationSelectionOpen] = useState(false);

  // State Selectors
  const medicationPadItem = useMedicationPadItemSelector(medicationPadItemId);

  // State Actions
  const useUpdateMedicationPadItemById = useAppStore.use.updateMedicationPadItemById;
  const updateMedicationPadItemById = useUpdateMedicationPadItemById();

  const isInvalidSelection = selectedMedication ? isStockDecoration(selectedMedication.decoration) : false;

  // TODO: duplicate in MedicationPadItemEditor (to be addressed in another PR)
  const initialReissueMedication = useRef<FilteredProduct | undefined>(undefined);

  useEffect(() => {
    if (medicationPadItem.reissueStatus?.isReissued && !initialReissueMedication.current && selectedMedication) {
      initialReissueMedication.current = selectedMedication;
    }
  }, [medicationPadItem.reissueStatus?.isReissued, selectedMedication]);

  const { data: productSurveyData } = useGetProductSurvey(doctorId);
  const firstQuestion = productSurveyData?.survey?.[0]?.question ?? {
    value: 0,
    name: '',
    subText: '',
    answers: [],
  };

  const productSelectionFeedbackProps: ProductSelectionFeedbackProps = {
    label: SHOW_MORE_PRODUCTS_BUTTON_LABEL,
    subText: firstQuestion?.subText,
    questionId: firstQuestion?.value,
    title: firstQuestion?.name,
    answers: firstQuestion?.answers,
  };

  const handleSurveyFeedbackChange = (answerId: number, randomAnswers: RadioOption[]) => {
    setIsMedicationSelectionOpen(true);
    setShowViewMoreProducts(false);
    if (answerId < 1) {
      logger.error('handleFeedbackChange:invalid answer id', {
        answerId,
      });
      return;
    }
    const updatedPrescriptionItem: MedicationPadItemType = {
      ...medicationPadItem,
      moreProductsLoaded: true,
      feedback: {
        questionAsked: productSelectionFeedbackProps.questionId,
        answerSelected: answerId,
        answerDisplayPositions: getAnswerDisplayPositions(productSelectionFeedbackProps.answers, randomAnswers),
      },
    };
    updateMedicationPadItemById(updatedPrescriptionItem);
  };

  const handleMedicationChange = useCallback(
    (changedSelection: FilteredProduct) => {
      // validate - move this part to shared util function
      if (changedSelection.id === medicationPadItem.medicationPadItem?.productId) {
        // same thing, don't dispatch anything
        return;
      }
      // If the user is changing a reissued medication, move it to cancel

      // validate for OOS - clear out previous selection if OOS selected.

      const { attributes } = changedSelection;
      const updatedPrescriptionItemDetail = validateAndUpdateMedicationPadItemDetailUtil(
        medicationPadItem.medicationPadItem,
        {
          ...getPadItemDetailFromProduct(changedSelection, getFormulationFilter(selectedFilters), prescriptionPadRules),
          repeats: attributes?.repeats?.default ?? PRODUCT_REPEATS_DEFAULT,
          interval: attributes?.interval?.default ?? PRODUCT_INTERVAL_DEFAULT,
          quantity: attributes?.quantity?.default ?? PRODUCT_QUANTITY_DEFAULT,
        },
      );

      if (!updatedPrescriptionItemDetail) {
        logger.error('handleMedicationChange: validation failed', {
          updatedPrescriptionItemDetail,
        });
        return;
      }

      // check for ranking tiers and feedback
      const isTopRecommended =
        isRankDecoration(changedSelection.decoration) && changedSelection.decoration.tier === RankTier.First;
      const isInitialMedication = initialReissueMedication.current?.id === changedSelection.id;
      let feedback = medicationPadItem.feedback ? { ...medicationPadItem.feedback } : undefined;

      if ((isTopRecommended || isInitialMedication) && !medicationPadItem.reissueStatus) {
        feedback = undefined;
        setShowViewMoreProducts(true);
      }

      const updatedPrescriptionItem: MedicationPadItemType = {
        ...medicationPadItem,
        medicationPadItem: updatedPrescriptionItemDetail,
        filters: selectedFilters,
        feedback,
      };
      updateMedicationPadItemById(updatedPrescriptionItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [medicationPadItem.reissueStatus, medicationPadItem, selectedFilters, prescriptionPadRules],
  );

  const productSelectionValue = useMemo(
    () => (selectedMedication ? mapPricingSchemaToProductCard(selectedMedication, selectedPriceTier) : undefined),
    [selectedMedication, selectedPriceTier],
  );

  const productSelectionOptions = useMemo(
    () => filteredMedicationOptions.map((product) => mapPricingSchemaToProductCard(product, selectedPriceTier)),
    [filteredMedicationOptions, selectedPriceTier],
  );

  const keepProductSelectionOpen = isMedicationSelectionOpen ? { open: true } : null;
  const surveyFeedbackElement = [
    showViewMoreProducts &&
      !medicationPadItem.moreProductsLoaded &&
      medicationOptions.length > recommendedMedicationsCount && (
        <ProductSelectionFeedback
          key={`productSelectFeedback-${medicationPadItemId}`}
          {...productSelectionFeedbackProps}
          onConfirm={handleSurveyFeedbackChange}
        />
      ),
    medicationOptions.length < recommendedMedicationsCount && (
      <Alert key={`all-product-shown-${medicationPadItemId}`} severity="info">
        All products are showing
      </Alert>
    ),
  ];

  return (
    <ProductSelection
      {...keepProductSelectionOpen}
      value={productSelectionValue}
      options={productSelectionOptions}
      onChange={handleMedicationChange}
      onClose={() => setIsMedicationSelectionOpen(false)}
      onPreviewIconClick={(medication) => {
        setIsMedicationSelectionOpen(false);
        handlePreviewIconClick?.(medication);
      }}
      MenuProps={{
        sx: {
          ['& .MuiSvgIcon-root']: {
            color: 'orange.main',
          },
          overflowY: 'scroll',
        },
      }}
      bottomElement={surveyFeedbackElement}
      enabledMedicationId={initialReissueMedication.current?.id ?? selectedMedication?.id}
      error={isInvalidSelection}
      showPricing={showPricing}
    />
  );
};
